import { useState } from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Warning from 'design-system/src/assets/icons/warning.svg?react';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import ChevronUp from 'design-system/src/assets/icons/chevronUp.svg?react';
import { formatStandardDateLocaleString } from 'shared-utils/utils/formatDate';
import type { MergedEligibilityRecord } from '../types';

type MergedEligibilityBannerProps = {
  /** Merged eligibility records to display */
  mergedEligibilities?: MergedEligibilityRecord[];
};

const BANNER_CLASSES = 'mb-4 rounded-medium border border-solid border-surface-300 bg-surface-200 p-4';
const TOGGLE_BUTTON_CLASSES = 'flex w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left';
const COUNT_BADGE_CLASSES = 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500 text-white';
const CARD_CLASSES = 'rounded-medium border-2 border-solid border-surface-300 bg-white px-5 py-3';
const CARD_LIST_CLASSES = 'mt-3 flex max-h-64 flex-col gap-2 overflow-y-auto pr-2';

// Prefer payer_name, then plan_name, then pbm_name
const getInsuranceName = (record: MergedEligibilityRecord): string => {
  return record.payer_name || record.plan_name || record.pbm_name || 'Unknown Plan';
};

const FieldValue = ({ label, value }: { label: string; value: string | null }) => (
  <div>
    <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-500)">
      <strong>{label}</strong>
    </Typography>
    <div className="mt-1">
      <Typography styledAs="bodySmallDMSans" color={value ? undefined : 'var(--red-500)'}>
        {value ?? '(missing)'}
      </Typography>
    </div>
  </div>
);

const EligibilityCard = ({ record }: { record: MergedEligibilityRecord }) => (
  <div className={CARD_CLASSES}>
    <div className="flex items-center justify-between py-3">
      <Typography styledAs="bodyLargeDMSans" color="var(--grayscale-600)">
        <strong>{getInsuranceName(record)}</strong>
      </Typography>
      <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
        {record.last_verified_at ? `Attempted ${formatStandardDateLocaleString(record.last_verified_at)}` : '—'}
      </Typography>
    </div>
    <hr className="my-2 border-surface-100" />
    <div className="grid grid-cols-4 gap-x-4">
      <FieldValue label="Cardholder ID" value={record.cardholder_id} />
      <FieldValue label="BIN #" value={record.bin} />
      <FieldValue label="Group #" value={record.group_id} />
      <FieldValue label="PCN #" value={record.pcn} />
    </div>
  </div>
);

/**
 * Displays a warning banner showing merged eligibility records that were already
 * attempted, so the provider can see what insurance info is on file.
 */
export const MergedEligibilityBanner = ({ mergedEligibilities }: MergedEligibilityBannerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!mergedEligibilities || mergedEligibilities.length === 0) {
    return null;
  }

  const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <div className={BANNER_CLASSES} role="status">
      <button
        type="button"
        className={TOGGLE_BUTTON_CLASSES}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <Warning height="20px" width="20px" className="shrink-0 text-red-500" aria-hidden />
        <div className="flex-1">
          <Typography styledAs="bodySmallDMSans">
            <strong>Insurance records on file were attempted ({mergedEligibilities.length})</strong>
          </Typography>
          <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
            Please check the existing insurance records before adding new information.
          </Typography>
        </div>
        <span className={COUNT_BADGE_CLASSES}>
          <Typography styledAs="bodyExtraExtraSmallDMSans" color="white">
            <strong>{mergedEligibilities.length}</strong>
          </Typography>
        </span>
        <ChevronIcon height="16px" width="16px" className="shrink-0 text-grayscale-500" aria-hidden />
      </button>
      {isExpanded && (
        <div className={CARD_LIST_CLASSES}>
          {mergedEligibilities.map((record) => (
            <EligibilityCard key={record.id} record={record} />
          ))}
        </div>
      )}
    </div>
  );
};
